import React, { useEffect, useLayoutEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "./AuthContext";
import { useUserProfile } from "./UserProfileContext";
import { createUsageTracker, usageRoute } from "../lib/usageTracker";
import { createUsageQueue } from "../lib/usageQueue";

import { UsageContext } from "./UsageAnalyticsContext";
const uuid = () => {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
const inForeground = () => document.visibilityState === "visible" && document.hasFocus();

function createRuntime(uid) {
  let storage;
  try { storage = window.localStorage; } catch (_) { /* In-memory retry is still available. */ }
  let tracker;
  const record = httpsCallable(getFunctions(), "recordUsageIntervals");
  const queue = createUsageQueue({
    uid, storage,
    isCurrentUser: () => getAuth().currentUser?.uid === uid,
    send: async intervals => {
      if (getAuth().currentUser?.uid !== uid) throw new Error("Account changed");
      return (await record({ accountUid: uid, intervals })).data;
    },
    onLoss: count => tracker.recordLoss({ droppedIntervals: count }),
  });
  tracker = createUsageTracker({
    now: () => performance.now(), wallNow: Date.now, uuid,
    context: { screen: null, videoId: null, aiAccess: "unknown" },
    emit: interval => { queue.push(interval); void queue.drain(); },
  });
  return {
    tracker, queue,
    setContext: next => tracker.setContext(next),
    playback: state => tracker.playback(state),
  };
}

// Mounted once inside AccountSession. Account changes flush to the previous
// account's queue; its events can never be submitted with another user's token.
export function UsageAnalyticsProvider({ children }) {
  const { user } = useAuth();
  const uid = user?.uid;
  const { profile, loading, error, authoritative } = useUserProfile();
  const { pathname } = useLocation();
  const runtime = useMemo(() => uid ? createRuntime(uid) : null, [uid]);
  const { screen, videoId } = usageRoute(pathname);
  const aiAccess = !profile || loading || error || !authoritative ? "unknown"
    : profile.aiFeaturesEnabled === undefined || profile.aiFeaturesEnabled === true ? "enabled" : "disabled";

  useLayoutEffect(() => {
    runtime?.setContext({ screen, videoId, aiAccess });
  }, [runtime, screen, videoId, aiAccess]);

  useEffect(() => {
    if (!runtime) return undefined;
    const { tracker, queue } = runtime;
    const foreground = () => tracker.setForeground(inForeground());
    const interact = () => { if (inForeground()) tracker.interact(); };
    const flush = () => { tracker.flush(); void queue.drain(); };
    const hide = () => { tracker.setForeground(false); flush(); };
    const resume = () => { foreground(); void queue.drain(); };
    const onVisibility = () => { foreground(); if (document.visibilityState !== "visible") flush(); };
    queue.start();
    foreground();
    void queue.drain();
    const timer = setInterval(() => { tracker.tick(); void queue.drain(); }, 1000);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", foreground);
    window.addEventListener("blur", foreground);
    window.addEventListener("pagehide", hide);
    window.addEventListener("pageshow", resume);
    window.addEventListener("online", resume);
    const interactions = ["pointerdown", "keydown", "scroll", "touchstart"];
    interactions.forEach(event => window.addEventListener(event, interact, { passive: true, capture: true }));
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", foreground);
      window.removeEventListener("blur", foreground);
      window.removeEventListener("pagehide", hide);
      window.removeEventListener("pageshow", resume);
      window.removeEventListener("online", resume);
      interactions.forEach(event => window.removeEventListener(event, interact, true));
      flush(); queue.stop();
    };
  }, [runtime]);
  const value = useMemo(() => ({ runtime }), [runtime]);
  return <UsageContext.Provider value={value}>{children}</UsageContext.Provider>;
}

